import React, { type PropsWithChildren, useCallback, useMemo } from 'react';

import {
  ResetPasswordDocument,
  UserSessionDocument,
  ValidateResetPasswordTokenDocument,
} from './__generated__/graphql';
import {
  AuthContext,
  type AuthContextState,
  type AuthenticationState,
  type LoginCredentials,
  type LoginResult,
} from './AuthContext';
import { upsertMarketingSmsConsent as upsertMarketingSmsConsentRequest } from './commPreferences';
import { DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE } from './ctx/initAuthContext';
import { type GraphQLClient, useGraphqlQuery } from './graphql/client';
import { parseAuthentication, type TrackError } from './helpers';
import {
  logIn as logInRequest,
  loginWithPasswordlessToken as loginWithPasswordlessTokenRequest,
  register as registerRequest,
  requestHumanStatus as requestHumanStatusRequest,
  requestPasswordReset as requestPasswordResetRequest,
} from './sessionClient';

export type AuthBaseUrlContextProviderProps = PropsWithChildren<{
  authBaseUrl: string;
  client: GraphQLClient;
  fetch?: typeof fetch;
  trackError?: TrackError;
  /** See `AuthContextState.analyticsPrefix` — the APP segment for this consumer. */
  analyticsPrefix: string;
}>;

const normalizeAuthBaseUrl = (authBaseUrl: string): string => authBaseUrl.replace(/\/+$/, '');

/** TODO: Add documentation */
export const AuthBaseUrlContextProvider: React.FC<AuthBaseUrlContextProviderProps> = ({
  trackError = () => {},
  client,
  fetch: fetchFn = fetch,
  authBaseUrl,
  analyticsPrefix,
  children,
}) => {
  const resolvedAuthBaseUrl = normalizeAuthBaseUrl(authBaseUrl);

  // TODO: Check if we have cookie so we can possibly skip the network call.
  const queryResult = useGraphqlQuery(client, UserSessionDocument, {});

  const logout: AuthContextState['logout'] = useCallback(() => {
    return fetchFn(`${resolvedAuthBaseUrl}/session/sign-out`, {
      method: 'GET',
      credentials: 'include',
    })
      .then(() => {
        window.location.reload();
      })
      .catch((e) => {
        trackError('logout', e);
        window.location.assign(`${resolvedAuthBaseUrl}/logout`);
      });
  }, [fetchFn, resolvedAuthBaseUrl, trackError]);

  const validateResetPasswordToken = async (resetPasswordToken: string) => {
    try {
      const response = await client.query(ValidateResetPasswordTokenDocument, {
        resetPasswordToken,
      });

      return {
        success: !!response?.data?.webPassword?.validateResetPasswordToken?.valid,
      };
    } catch (err) {
      return {
        success: false,
        displayErrorMessage: 'Error Validating Reset Password Token',
      };
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      const response = await client.query(ResetPasswordDocument, {
        resetPasswordToken: token,
        newPassword: password,
      });

      if (!response.error) {
        return {
          success: true,
        };
      }

      return {
        success: false,
      };
    } catch (err) {
      return {
        success: false,
      };
    }
  };

  const verifyLogin: AuthContextState['verifyLogin'] = useCallback(
    (credentials) => callVerifyLogin(resolvedAuthBaseUrl, fetchFn, trackError, credentials),
    [fetchFn, resolvedAuthBaseUrl, trackError],
  );

  const logIn: AuthContextState['logIn'] = useCallback(
    (credentials) => logInRequest(resolvedAuthBaseUrl, credentials, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const register: AuthContextState['register'] = useCallback(
    (params) => registerRequest(resolvedAuthBaseUrl, params, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const loginWithPasswordlessToken: AuthContextState['loginWithPasswordlessToken'] = useCallback(
    (params) => loginWithPasswordlessTokenRequest(resolvedAuthBaseUrl, params, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const requestHumanStatus: AuthContextState['requestHumanStatus'] = useCallback(
    (email) => requestHumanStatusRequest(resolvedAuthBaseUrl, email, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const requestPasswordReset: AuthContextState['requestPasswordReset'] = useCallback(
    (email) => requestPasswordResetRequest(resolvedAuthBaseUrl, email, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const upsertMarketingSmsConsent: AuthContextState['upsertMarketingSmsConsent'] = useCallback(
    async (consent) => {
      const result = await upsertMarketingSmsConsentRequest(client, consent);
      if (!result.success) {
        // Surface the drop so systemic consent loss is detectable — a declining
        // user whose opt-out silently fails must not vanish. Registration is
        // unaffected: the caller treats this write as best-effort. (Staging/dev
        // resolve `no-customer` by construction: the fresh session cookie is set
        // on the Rails host but does not reach Athena there.)
        trackError('marketing-sms-consent', new Error('marketing SMS consent write failed'), {
          reason: result.reason,
        });
      }
      return result;
    },
    [client, trackError],
  );

  const authentication: AuthenticationState = useMemo(
    () =>
      parseAuthentication(
        { data: queryResult.data, loading: queryResult.loading, error: queryResult.error },
        trackError,
      ),
    // trackError isn't needed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryResult.loading, queryResult.data, queryResult.error, logout],
  );

  return (
    <AuthContext.Provider
      value={{
        analyticsPrefix,
        authentication,
        logout,
        refresh: queryResult.refresh,
        verifyLogin,
        validateResetPasswordToken,
        resetPassword,
        logIn,
        register,
        loginWithPasswordlessToken,
        requestHumanStatus,
        requestPasswordReset,
        upsertMarketingSmsConsent,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

async function callVerifyLogin(
  authBaseUrl: string,
  fetchFn: typeof fetch,
  trackError: TrackError,
  credentials: LoginCredentials,
): Promise<LoginResult> {
  let payload: unknown;
  if ('googleIdToken' in credentials) {
    payload = { google_id_token: credentials.googleIdToken };
  } else if ('facebookIdToken' in credentials) {
    payload = { facebook_id_token: credentials.facebookIdToken };
  } else {
    payload = {
      human: {
        email: credentials.email,
        password: credentials.password,
      },
    };
  }

  // See `verify_login` at
  // https://github.com/opendoor-labs/web/blob/master/app/controllers/humans/sessions_controller.rb
  let resp;
  try {
    resp = await fetchFn(`${authBaseUrl}/verify_login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    trackError('login', e as Error);
    return { success: false, displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE };
  }

  if (resp.ok) {
    return { success: true };
  } else {
    let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;
    try {
      const json = await resp.json();
      if ('error' in json) {
        displayErrorMessage = json['error'];
      }
    } catch (e) {
      trackError('login error without error message', e as Error);
    }
    return {
      success: false,
      displayErrorMessage,
    };
  }
}
